// src/simulators/NetworkChallengeSimulator.jsx
import React, { useState, useEffect } from 'react';
import { makeStyles } from 'tss-react/mui';
import axios from 'axios';

// === CRITICAL: VERIFY THESE PATHS ===
import NetworkMonitor from './NetworkMonitor';
import PacketAnalyzer from './PacketAnalyzer';
import FirewallSimulator from './FirewallSimulator';  // ← MUST BE CORRECT
import StepNavigator from './StepNavigator';

const useStyles = makeStyles()((theme) => ({
  container: { padding: theme.spacing(3), maxWidth: 1200, margin: '0 auto' },
  header: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: theme.spacing(2), borderBottom: `1px solid ${theme.palette.divider}`, paddingBottom: theme.spacing(1) },
  score: { fontWeight: 600, color: theme.palette.primary.main },
  body: { marginTop: theme.spacing(3) },
  completion: { textAlign: 'center', marginTop: theme.spacing(4), padding: theme.spacing(3), backgroundColor: theme.palette.success.light, borderRadius: theme.shape.borderRadius, color: theme.palette.success.contrastText },
  loading: { textAlign: 'center', padding: theme.spacing(5), fontSize: '1.2rem', color: theme.palette.text.secondary },
  error: { color: 'red', background: '#fee', padding: theme.spacing(2), borderRadius: 4, margin: theme.spacing(2, 0) },
}));

const NetworkSecuritySimulator = ({ challengeId, scenarios: incomingScenarios, challenge: challengeProp, onComplete, estimatedTime }) => {
  const { classes } = useStyles();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentStepIdx, setCurrentStepIdx] = useState(0);
  const [score, setScore] = useState(0);

useEffect(() => {
  // If scenarios are passed from parent
  if (Array.isArray(incomingScenarios) && incomingScenarios.length > 0) {
    const syntheticData = {
      challenge: challengeProp || { title: "Network Security Challenge" },
      steps: incomingScenarios.map((sc, i) => {
        // Wrap raw scenario in `content`
        const content = { ...sc };

        // Ensure `type` is set for fallback mapping
        if (sc.scenario) {
          content.type = sc.scenario; // ← ADD THIS
        }

        return {
          id: `step-${i}`,
          content,
          points: 1
        };
      })
    };
    setData(syntheticData);
    setLoading(false);
    return;
  }

  // Fallback to fetching by challengeId (if present)
  if (challengeId) {
    const fetchData = async () => {
      try {
        setLoading(true);
        const res = await axios.get(`/api/challenges/${challengeId}/simulator`);
        setData(res.data);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }
}, [challengeId, incomingScenarios, challengeProp]);


  if (loading) return <div className={classes.loading}>Loading challenge...</div>;
  if (error) return <div className={classes.error}>Error: {error}</div>;
  if (!data) return <div className={classes.error}>No data</div>;

  const { challenge, steps } = data;
  const step = steps[currentStepIdx];

  let metadata = challenge?.metadata || challengeProp?.metadata;

  // Parse if it's a JSON string
  if (typeof metadata === 'string') {
    try {
      metadata = JSON.parse(metadata);
    } catch (e) {
      console.warn('Failed to parse metadata JSON', e);
      metadata = {};
    }
  }

  // === DEBUG: Log what we're trying to render ===
  console.log('Simulator type from metadata:', challenge.metadata?.simulator);
  console.log('Available simulators:', { NetworkMonitor, PacketAnalyzer, FirewallSimulator });


  let simulatorKey = '';

  // 1. metadata.simulator (from DB)
  if (metadata?.simulator) {
    simulatorKey = metadata.simulator;
  }
  // 2. challenge.simulatorType
  else if (challenge?.simulatorType) {
    simulatorKey = challenge.simulatorType;
  } else if (challengeProp?.simulatorType) {
    simulatorKey = challengeProp.simulatorType;
  }
  // 3. step.content.simulator
  else if (step?.content?.simulator) {
    simulatorKey = step.content.simulator;
  }

  // Normalize
  const normalize = (str) => str.toLowerCase().replace(/-/g, '').replace(/_/g, '');
  const normalizedKey = simulatorKey ? normalize(simulatorKey) : '';

  const simulatorLookup = {
    // === FROM METADATA ===
    networkmonitor: NetworkMonitor,
    'network-monitor': NetworkMonitor,
    'network_security_monitor': NetworkMonitor,
    'network-security-monitor': NetworkMonitor,  // ← ADD THIS
    network_monitor_component: NetworkMonitor,
    networksecuritymonitor: NetworkMonitor,

    // === FROM simulatorType (passed from container) ===
    'network-security-monitor': NetworkMonitor,  // ← CRITICAL: ADD THIS

    packetanalyzer: PacketAnalyzer,
    'packet-analyzer': PacketAnalyzer,
    packet_analyzer: PacketAnalyzer,

    firewallsimulator: FirewallSimulator,
    'firewall-simulator': FirewallSimulator,
    firewall: FirewallSimulator,
  };

  let SimulatorComponent = simulatorLookup[normalizedKey];

  if (!SimulatorComponent && step?.content?.type) {
    const t = step.content.type.toLowerCase();
    if (t.includes('scan') || t.includes('traffic') || t.includes('monitor') || t.includes('dns')) {
      SimulatorComponent = NetworkMonitor;
    } else if (t.includes('packet') || t.includes('analyze')) {
      SimulatorComponent = PacketAnalyzer;
    } else if (t.includes('firewall') || t.includes('rules')) {
      SimulatorComponent = FirewallSimulator;
    }
  }

// === LAST RESORT ===
  if (!SimulatorComponent) {
    SimulatorComponent = NetworkMonitor;
  }

  const handleStepComplete = (points) => {
    setScore(s => s + points);
    if (currentStepIdx < steps.length - 1) {
      setCurrentStepIdx(i => i + 1);
    } else {
      const total = steps.reduce((a, s) => a + (s.points || 0), 0);
      onComplete({
        correct: score + points,
        total,
        score: Math.round(((score + points) / total) * 100),
      });
    }
  };

  return (
    <div className={classes.container}>
      <header className={classes.header}>
        <div className={classes.score}>
          Score: {score} / {steps.reduce((a, s) => a + (s.points || 0), 0)}
        </div>
      </header>

      

      <div className={classes.body}>
        <SimulatorComponent
          step={step}
          onComplete={handleStepComplete}
          metadata={challenge.metadata}
        />
      </div>
      {/* <StepNavigator steps={steps} current={currentStepIdx} /> */}
    </div>
  );
};

export default NetworkSecuritySimulator;